// Firebase Cloud Functions API service
import { auth, appCheck } from '../firebase';
import { signInAnonymously } from 'firebase/auth';
import { getToken } from 'firebase/app-check';

const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'luvf-634c7';
const API_BASE_URL =
  process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  `https://us-central1-${projectId}.cloudfunctions.net`;

/** Firebase Auth ID token (required) + App Check token (when configured) for HTTPS Cloud Functions.
 *  Anonymous sign-in is used when no account is signed in yet, so guest collage flows still work. */
async function buildCloudFunctionFetchHeaders(options = {}) {
  const { includeJsonContentType = true, idToken: idTokenOverride = null } = options;
  let idToken = typeof idTokenOverride === 'string' && idTokenOverride ? idTokenOverride : null;
  /* An explicit token must not sign the browser in (email signup stays signed-out in the UI
     while SAVE still authorizes as that account). */
  if (!idToken) {
    await auth.authStateReady();
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }
    idToken = await auth.currentUser.getIdToken();
  }
  const headers = {
    Authorization: `Bearer ${idToken}`,
    Accept: 'application/json',
  };
  if (appCheck) {
    try {
      const { token } = await getToken(appCheck, false);
      if (token) headers['X-Firebase-AppCheck'] = token;
    } catch (e) {
      console.warn('App Check token fetch failed (continuing without):', e?.message || e);
    }
  }
  if (includeJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

// Generate conversational response via Firebase Function
export const generateConversationalResponse = async (conversationHistory, userDescription, userTier = 'free') => {
  try {
    const response = await fetch(`${API_BASE_URL}/generateConversationalResponse`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({
        conversationHistory,
        userDescription,
        userTier
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success) {
      return data.response;
    } else {
      throw new Error(data.error || 'Unknown error');
    }
  } catch (error) {
    console.error('Firebase API error for conversational response:', error);
    
    // Fallback responses for conversational flow
    const fallbacks = [
      "That's such a vibe! What's the most important quality you're looking for in someone?",
      "I love that energy! Tell me about your ideal first date.",
      "That sounds amazing! What kind of personality traits attract you most?",
      "So good! What's something you absolutely need to have in common with a partner?",
      "Love it! What's your idea of the perfect relationship dynamic?",
      "That's fire! What's something that would be a total deal-breaker for you?",
      "So real! What's the most important thing you want to share with someone special?",
      "That's the energy! What kind of lifestyle are you looking for in a partner?",
      "Perfect! What's something you're really passionate about that you'd want to share?"
    ];
    
    const usedResponses = conversationHistory
      .filter(msg => msg.type === 'bot')
      .map(msg => msg.content);
    
    const availableFallbacks = fallbacks.filter(fallback => 
      !usedResponses.some(used => used.includes(fallback.split('!')[0]))
    );
    
    return availableFallbacks[0] || fallbacks[0];
  }
};

// Generate vibe analysis via Firebase Function
export const generateVibeAnalysis = async (conversationHistory, userDescription, userTier = 'free') => {
  try {
    const response = await fetch(`${API_BASE_URL}/generateVibeAnalysis`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({
        conversationHistory,
        userDescription,
        userTier
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success) {
      return {
        response: data.response,
        bookTitle: data.bookTitle || 'Unknown Book',
        author: data.author || 'Unknown Author'
      };
    } else {
      throw new Error(data.error || 'Unknown error');
    }
  } catch (error) {
    console.error('Firebase API error for vibe analysis:', error);
    
    // Fallback response
    return {
      response: 'BOOK_TITLE: The Seven Husbands of Evelyn Hugo\nAUTHOR: Taylor Jenkins Reid',
      bookTitle: 'The Seven Husbands of Evelyn Hugo',
      author: 'Taylor Jenkins Reid'
    };
  }
};

// Generate partner images via Firebase Function (OpenAI DALL-E)
export const generatePartnerImages = async (activities, emojis, userTier = 'free') => {
  try {
    const response = await fetch(`${API_BASE_URL}/generatePartnerImages`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({
        activities,
        emojis,
        userTier
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('HTTP error response:', {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorData.error || errorData.details || response.statusText}`);
    }

    const data = await response.json();
    
    if (data.success) {
      return {
        image: data.image
      };
    } else {
      console.error('API returned error:', data);
      throw new Error(data.error || data.details || 'Unknown error');
    }
  } catch (error) {
    console.error('Firebase API error for image generation:', error);
    console.error('Full error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    throw error;
  }
};

// Generate partner images via Firebase Function (Replicate flux-kontext-pro)
export const generatePartnerImagesReplicate = async (activities, emojis, userTier = 'free', numImages = 1) => {
  try {
    const response = await fetch(`${API_BASE_URL}/generatePartnerImagesReplicate`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({
        activities,
        emojis,
        userTier,
        numImages
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('HTTP error response:', {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorData.error || errorData.details || response.statusText}`);
    }

    const data = await response.json();
    
    if (data.success) {
      return {
        images: data.images,
        generatedCount: data.generatedCount
      };
    } else {
      console.error('API returned error:', data);
      throw new Error(data.error || data.details || 'Unknown error');
    }
  } catch (error) {
    console.error('Firebase API error for Replicate image generation:', error);
    console.error('Full error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    // Return empty result on error
    return {
      images: [],
      generatedCount: 0,
      error: error.message
    };
  }
};

// Analyze image via Firebase Function (Replicate API)
// TODO: Implement the actual Firebase Cloud Function endpoint
export const analyzeImageReplicate = async (imageFile, userTier = 'free') => {
  try {
    // Convert image file to base64 for transmission
    const base64Image = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data:image/...;base64, prefix
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    });

    const response = await fetch(`${API_BASE_URL}/analyzeImageReplicate`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({
        image: base64Image,
        imageType: imageFile.type,
        userTier
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('HTTP error response:', {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorData.error || errorData.details || response.statusText}`);
    }

    const data = await response.json();
    
    if (data.success) {
      return {
        analysis: data.analysis,
        insights: data.insights || []
      };
    } else {
      console.error('API returned error:', data);
      throw new Error(data.error || data.details || 'Unknown error');
    }
  } catch (error) {
    console.error('Firebase API error for image analysis:', error);
    console.error('Full error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    throw error;
  }
};

// Analyze ex image via Firebase Function (Replicate API)
// TODO: Implement the actual Firebase Cloud Function endpoint
export const analyzeExImageReplicate = async (imageFile, userTier = 'free') => {
  try {
    // Convert image file to base64 for transmission
    const base64Image = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data:image/...;base64, prefix
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    });

    const response = await fetch(`${API_BASE_URL}/analyzeExImageReplicate`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({
        image: base64Image,
        imageType: imageFile.type,
        userTier
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('HTTP error response:', {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorData.error || errorData.details || response.statusText}`);
    }

    const data = await response.json();
    
    if (data.success) {
      return {
        analysis: data.analysis,
        insights: data.insights || []
      };
    } else {
      console.error('API returned error:', data);
      throw new Error(data.error || data.details || 'Unknown error');
    }
  } catch (error) {
    console.error('Firebase API error for ex image analysis:', error);
    console.error('Full error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    throw error;
  }
};

// Generate Jungian partner profile via Firebase Function (Replicate openai/gpt-5-nano)
export const generateJungianProfile = async (prompt, userTier = 'free') => {
  try {
    const response = await fetch(`${API_BASE_URL}/generateJungianProfile`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({ prompt, userTier }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.success) {
      return data.analysis;
    }
    throw new Error(data.error || data.details || 'Unknown error');
  } catch (error) {
    console.error('Firebase API error for Jungian profile:', error);
    throw error;
  }
};

// Generate one-word descriptor for "their word" via Firebase Function (Replicate gpt-5-nano), using musicians + gender from survey
export const generateOneWordDescriptor = async (musicians, gender, userTier = 'free') => {
  try {
    const response = await fetch(`${API_BASE_URL}/generateOneWordDescriptor`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({ musicians: musicians ?? [], gender: gender ?? '' }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.success) {
      return data.descriptor;
    }
    throw new Error(data.error || data.details || 'Unknown error');
  } catch (error) {
    console.error('Firebase API error for one-word descriptor:', error);
    throw error;
  }
};

// Generate partner cutout image via Firebase Function (Replicate google/nano-banana), using Jungian analysis + random reference from Firebase Storage (cutouts/reference/2.png, 13.png, 8.png)
export const generatePartnerCutoutNanoBanana = async (jungianAnalysis, userTier = 'free', lookingFor = '') => {
  try {
    const response = await fetch(`${API_BASE_URL}/generatePartnerCutoutNanoBanana`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({ jungianAnalysis, userTier, lookingFor }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.success) {
      return data.imageUrl;
    }
    throw new Error(data.error || data.details || 'Unknown error');
  } catch (error) {
    console.error('Firebase API error for partner cutout (nano-banana):', error);
    throw error;
  }
};

// Edit partner cutout via Firebase Function (Replicate google/nano-banana): current image + user edit prompt; optional maskImageUrl as second reference
export const editPartnerCutoutNanoBanana = async (imageUrl, userPrompt, maskImageUrl = null) => {
  try {
    const body = { imageUrl, userPrompt };
    if (maskImageUrl && typeof maskImageUrl === 'string') body.maskImageUrl = maskImageUrl;
    const response = await fetch(`${API_BASE_URL}/editPartnerCutoutNanoBanana`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.success) {
      return data.imageUrl;
    }
    throw new Error(data.error || data.details || 'Unknown error');
  } catch (error) {
    console.error('Firebase API error for partner cutout edit (nano-banana):', error);
    throw error;
  }
};

// Apply fox paper-cutout mask to a user photo via Cloud Function (server builds the edit prompt).
export const applyUserPhotoFoxMask = async (imageUrl) => {
  try {
    const response = await fetch(`${API_BASE_URL}/applyUserPhotoFoxMask`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({ imageUrl }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.success) {
      return data.imageUrl;
    }
    throw new Error(data.error || data.details || 'Unknown error');
  } catch (error) {
    console.error('Firebase API error for user photo fox mask:', error);
    throw error;
  }
};

// Generate brand image from text prompt via Firebase Function (Replicate google/nano-banana) - text-to-image
export const generateBrandImageNanoBanana = async (prompt) => {
  try {
    const response = await fetch(`${API_BASE_URL}/generateBrandImageNanoBanana`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({ prompt }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.success) {
      return data.imageUrl;
    }
    throw new Error(data.error || data.details || 'Unknown error');
  } catch (error) {
    console.error('Firebase API error for brand image (nano-banana):', error);
    throw error;
  }
};

// Search spirit GIFs via Firebase Function (proxies KLIPY API)
export const searchSpiritGifs = async (query = 'vibe', customerId = 'anonymous', perPage = 9, page = 1) => {
  const params = new URLSearchParams({
    q: query,
    per_page: String(perPage),
    page: String(page),
    customer_id: customerId,
  });

  const response = await fetch(`${API_BASE_URL}/searchSpiritGifs?${params}`, {
    method: 'GET',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders({ includeJsonContentType: false }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success) {
    return data.gifs;
  }
  throw new Error(data.error || 'Failed to fetch GIFs');
};

// Run the new-matches engine for the signed-in user (FoxChat daily-check retry)
export const computeMyNewMatches = async () => {
  const response = await fetch(`${API_BASE_URL}/computeMyNewMatches`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success) {
    return data.result;
  }
  throw new Error(data.error || 'Failed to compute matches');
};

/**
 * FoxChat daily check: ask the server to surface one VVTF match for this vibe period.
 * Gates, candidate verification, conversation + intro seeding, seen-flip and check-doc
 * stamp all happen server-side (functions/vvtfMatchInitiate.js).
 *
 * @param {string} periodKey Local 3am–3am vibe period (YYYY-MM-DD).
 * @param {{ another?: boolean }} [opts]
 *   `another` skips today's already-stamped match and surfaces the next unseen one.
 * @returns {Promise<
 *   | { status: 'gate', gate: 'verify' }
 *   | { status: 'noMatches' }
 *   | { status: 'match', conversationId: string, matchUid: string|null, nickname: string|null, foundAt: number, restored: boolean }
 * >}
 */
export const initiateMyVvtfMatch = async (periodKey, { another = false } = {}) => {
  const response = await fetch(`${API_BASE_URL}/initiateMyVvtfMatch`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify({
      periodKey,
      ...(another ? { another: true } : {}),
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success) {
    return data.result;
  }
  throw new Error(data.error || 'Failed to initiate match');
};

// Ensures the signed-in user has a stored aura color, assigning one server-side if
// missing. The color-selection methodology lives only in Cloud Functions, so the
// client never generates or derives it — it just reads the result. Returns { r, g, b }.
export const ensureMyAuraColor = async () => {
  const response = await fetch(`${API_BASE_URL}/ensureMyAuraColor`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success) {
    return data.auraColor;
  }
  throw new Error(data.error || 'Failed to assign aura color');
};

// Reports a collage edit so the server can drift the user's aura color. `tileChanges` is the
// per-category count from countCollageTileChanges; how far and which way the color moves is
// decided server-side, so nothing about the methodology is derivable here. Returns { r, g, b }.
export const driftMyAuraColor = async (tileChanges) => {
  const response = await fetch(`${API_BASE_URL}/driftMyAuraColor`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify({ tileChanges }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success) {
    return data.auraColor;
  }
  throw new Error(data.error || 'Failed to update aura color');
};

// Fox Chat temp vibe color. The server applies the same drift as a user-page edit and
// does not write users/{uid}. Returns opaque { auraColor, auraHsl } for this session.
export const previewAuraColor = async ({ tileChanges, auraColor, auraHsl } = {}) => {
  const response = await fetch(`${API_BASE_URL}/previewAuraColor`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify({ tileChanges, auraColor, auraHsl }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success && data.auraColor) {
    return {
      auraColor: data.auraColor,
      auraHsl: data.auraHsl || null,
    };
  }
  throw new Error(data.error || 'Failed to preview aura color');
};

// Fox Chat opening vibe. Server picks the color; nothing is written to users/{uid}.
export const assignPreviewAuraColor = async () => {
  const response = await fetch(`${API_BASE_URL}/assignPreviewAuraColor`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success && data.auraColor) {
    return {
      auraColor: data.auraColor,
      auraHsl: data.auraHsl || null,
    };
  }
  throw new Error(data.error || 'Failed to assign preview aura color');
};

// Search brand/stuff images via Firebase Function (proxies Brave Image Search API)
export const searchBrandImages = async (query, count = 9) => {
  const params = new URLSearchParams({
    q: query,
    count: String(count),
  });

  const response = await fetch(`${API_BASE_URL}/searchBrandImages?${params}`, {
    method: 'GET',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders({ includeJsonContentType: false }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success) {
    return data.results;
  }
  throw new Error(data.error || 'Failed to search images');
};

/** Copy a custom search-result image into Firebase Storage; returns the stable download URL. */
export const persistSearchResultImage = async (imageUrl, kind) => {
  const response = await fetch(`${API_BASE_URL}/persistSearchResultImage`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify({ imageUrl, kind }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success && typeof data.imageUrl === 'string' && data.imageUrl) {
    return data.imageUrl;
  }
  throw new Error(data.error || 'Failed to persist search image');
};

/** Whether `email` may sign up (@berkeley.edu or server-managed allowlist). Uses Auth + optional anon session. */
export const checkCalSignupEmailAllowed = async (email) => {
  const response = await fetch(`${API_BASE_URL}/checkCalSignupEmail`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify({ email }),
  });
  const data = await response.json().catch(() => ({}));
  if (response.status === 429) {
    const err = new Error(data.error || 'Too many email checks. Try again tomorrow.');
    err.code = data.code || 'rate_limited';
    throw err;
  }
  if (!response.ok) {
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }
  return data.allowed === true;
};

/** Whether the current signed-in Firebase user's Auth email has Cal campus access (berkeley.edu or server allowlist). */
export const checkCalAccessForSignedInAccount = async () => {
  const response = await fetch(`${API_BASE_URL}/checkCalAccessForSignedInAccount`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: '{}',
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }
  return data.hasAccess === true;
};

// Send welcome / set-password email via Postmark (called after sign-up).
// Optional `foxChatPoster`: in-progress FoxChat collage attached server-side to the new account
// (same mechanism as the password-reset attach path); response carries `attachedPosterId`.
export const sendWelcomeEmailApi = async (email, { foxChatPoster = null } = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/sendWelcomeEmailFunc`, {
      method: 'POST',
      mode: 'cors',
      headers: await buildCloudFunctionFetchHeaders(),
      body: JSON.stringify({
        email,
        ...(foxChatPoster && typeof foxChatPoster === 'object' ? { foxChatPoster } : {}),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('sendWelcomeEmailApi error:', error);
    throw error;
  }
};

/**
 * Attach a finished FoxChat collage to the account that just signed up by email.
 * Uses the ID token captured before sign-out — does not sign the browser back in
 * and does not send another email.
 */
export const attachFoxChatPosterApi = async (foxChatPoster, idToken, { skipSurvey = false } = {}) => {
  const response = await fetch(`${API_BASE_URL}/attachFoxChatPosterFunc`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders({ idToken }),
    body: JSON.stringify({
      foxChatPoster,
      ...(skipSurvey ? { skipSurvey: true } : {}),
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `HTTP error! status: ${response.status}`);
    err.status = response.status;
    err.code = data.code;
    throw err;
  }
  return data;
};

/**
 * After the set-password link is completed and the user is signed in: if the account
 * email is a .edu, approve the profile server-side (no separate verify-email step).
 */
export const approveEduAfterSetPasswordApi = async () => {
  const response = await fetch(`${API_BASE_URL}/approveEduAfterSetPassword`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: '{}',
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }
  return data;
};

/** Email a friend inviting them to sign up and share daily vibe checks. */
export const sendDailyVibeEmailInviteApi = async (friendEmail) => {
  const response = await fetch(`${API_BASE_URL}/sendDailyVibeEmailInviteFunc`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify({ friendEmail }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { ok: false, error: data.error || 'Could not send invite.' };
  }
  return { ok: true, ...data };
};

/** Accept pending daily-vibe email invites for the signed-in user. */
export const fulfillDailyVibeEmailInvitesApi = async ({ inviterUid = null } = {}) => {
  const response = await fetch(`${API_BASE_URL}/fulfillDailyVibeEmailInvitesFunc`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify(inviterUid ? { inviterUid } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { ok: false, error: data.error || 'Could not accept invite.' };
  }
  return { ok: true, ...data };
};

// Send password-reset email via Postmark (called from forgot-password flow).
// Optional `foxChatPoster`: canonical poster doc built client-side — server attaches to the account for this email when sent.
export const sendResetPasswordEmailApi = async (email, options = {}) => {
  const { foxChatPoster } = options;
  const body = { email };
  if (foxChatPoster && typeof foxChatPoster === 'object') {
    body.foxChatPoster = foxChatPoster;
  }
  const response = await fetch(`${API_BASE_URL}/sendResetPasswordEmailFunc`, {
    method: 'POST',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders(),
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `HTTP error! status: ${response.status}`);
    err.code = data.code;
    err.status = response.status;
    if (data.retryAfterSeconds != null) err.retryAfterSeconds = data.retryAfterSeconds;
    throw err;
  }
  return data;
};

// Search places via Firebase Function (proxies Google Places API New)
export const searchPlaces = async (query, pageToken = '') => {
  const params = new URLSearchParams({ q: query });
  if (pageToken) params.set('pageToken', pageToken);

  const response = await fetch(`${API_BASE_URL}/searchPlaces?${params}`, {
    method: 'GET',
    mode: 'cors',
    headers: await buildCloudFunctionFetchHeaders({ includeJsonContentType: false }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.success) {
    const results = data.results || [];
    results.nextPageToken = data.nextPageToken || '';
    return results;
  }
  throw new Error(data.error || 'Failed to search places');
};
